/**
 * GridCell Component
 *
 * Individual cell in the battleship grid
 * - Displays cell state (untouched, hit, miss, attacking)
 * - Handles click interactions
 * - Accessible with proper aria labels
 * - Touch-friendly sizing (44x44px minimum)
 */

import HitMarker from './HitMarker';
import MissMarker from './MissMarker';

export default function GridCell({
  coordinate,
  state,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isClickable = true,
}) {
  /**
   * Get CSS classes based on cell state
   */
  const getStateClasses = () => {
    const baseClasses =
      'w-11 h-11 border border-neon-cyan/30 transition-colors duration-200';

    switch (state) {
      case 'hit':
        return `${baseClasses} bg-neon-pink/20 shadow-glow-pink`;
      case 'miss':
        return `${baseClasses} bg-neon-blue/20`;
      case 'attacking':
        return `${baseClasses} bg-neon-yellow/30 attacking animate-pulse shadow-glow-yellow`;
      default:
        return `${baseClasses} bg-dark-purple/40 ${
          isClickable
            ? 'hover:bg-neon-cyan/20 hover:shadow-glow-cyan cursor-pointer'
            : ''
        }`;
    }
  };

  /**
   * Get accessible aria-label
   */
  const getAriaLabel = () => {
    if (state === 'hit') {
      return `${coordinate} - Hit`;
    }
    if (state === 'miss') {
      return `${coordinate} - Miss`;
    }
    if (state === 'attacking') {
      return `${coordinate} - Attacking...`;
    }
    return `${coordinate} - Untouched`;
  };

  const isDisabled = state !== 'untouched' || !isClickable;

  return (
    <button
      type="button"
      data-testid={`grid-cell-${coordinate}`}
      data-coordinate={coordinate}
      data-state={state}
      className={getStateClasses()}
      onClick={onClick}
      onMouseEnter={() => onMouseEnter && onMouseEnter(coordinate)}
      onMouseLeave={() => onMouseLeave && onMouseLeave()}
      aria-label={getAriaLabel()}
      disabled={isDisabled}
    >
      {/* Visual indicators with HitMarker and MissMarker components */}
      {state === 'hit' && (
        <div className="flex items-center justify-center h-full animate-hit">
          <HitMarker size="medium" />
        </div>
      )}
      {state === 'miss' && (
        <div className="flex items-center justify-center h-full animate-miss">
          <MissMarker size="medium" />
        </div>
      )}
    </button>
  );
}
